import cn from '@propertypal/shared/src/utils/cn';
import { useField, useFormikContext } from 'formik';
import React, { useState, useRef, useEffect, FocusEvent, useCallback, CSSProperties } from 'react';
import StyledLabel from '../_styles/StyledLabel';
import PropertyPalError from '../error/PropertyPalError';
import { FlexLabelWrapper, InputWrapper, RemainingChars, StyledTextAreaAutoResize } from './PropertyPalTextArea.style';

interface PropertyPalTextInputProps {
  containerStyle?: CSSProperties;
  label?: string | React.ReactNode;
  name: string;
  id?: string;
  placeholder?: string;
  onChange?: (value: string | null, name: string | null) => void;
  onBlur?: (value: string | null, name: string | null) => void;
  onFocus?: (value: string | null, name: string | null) => void;
  disabled?: boolean;
  maxLimit?: number;
  rows?: number;
  bg?: string;
  testID?: string;
  labelTestID?: string;
  className?: string;
  errClassName?: string;
}

const PropertyPalTextArea: React.FC<PropertyPalTextInputProps> = ({
  containerStyle,
  label,
  disabled,
  maxLimit,
  id,
  name,
  onChange,
  onBlur,
  onFocus,
  placeholder,
  rows,
  bg,
  testID,
  labelTestID,
  className,
  errClassName,
}) => {
  const [field, meta] = useField(name);

  const [charsLeft, setCharsLeft] = useState(maxLimit);

  const ref = useRef<HTMLTextAreaElement>(null);

  const formikContext = useFormikContext();

  const isInErrorState = meta?.touched && meta?.error != null;

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const val = e.currentTarget.value;
      if (onChange) onChange(val, field.name);
      if (maxLimit) {
        setCharsLeft(maxLimit - val.length);
      }
      field.onChange(e);
    },
    [onChange, field.onChange],
  );

  const onInputBlur = useCallback(
    (e: FocusEvent<HTMLTextAreaElement>) => {
      const val = e.currentTarget.value;
      if (onBlur) onBlur(val, field.name);

      field.onBlur(e);
    },
    [onBlur, field.onBlur],
  );

  const onInputFocus = useCallback(
    (e: FocusEvent<HTMLTextAreaElement>) => {
      const val = e.currentTarget.value;
      if (onFocus) onFocus(val, field.name);

      formikContext.setFieldTouched(field.name, true, true);
    },
    [onFocus],
  );

  useEffect(() => {
    if (field?.value && maxLimit) setCharsLeft(maxLimit - field.value?.length);
  }, []);

  return (
    <div style={containerStyle}>
      <FlexLabelWrapper>
        {label && (
          <StyledLabel data-testid={labelTestID || 'text-area-label'} boldLabel htmlFor={id || name}>
            {label}
          </StyledLabel>
        )}
        {maxLimit && <RemainingChars data-testid="text-area-chars">{charsLeft} characters left</RemainingChars>}
      </FlexLabelWrapper>
      <InputWrapper
        err={isInErrorState}
        bg={bg}
        data-testid="text-area-wrapper"
        className={cn(
          'pp-text-input',
          className,
          isInErrorState && 'pp-text-input-error',
          isInErrorState && errClassName,
        )}
      >
        <StyledTextAreaAutoResize
          ref={ref}
          maxLength={maxLimit}
          disabled={disabled}
          id={id}
          name={field.name}
          onChange={onInputChange}
          value={field.value}
          onBlur={onInputBlur}
          onFocus={onInputFocus}
          placeholder={placeholder}
          rows={rows}
          data-gramm_editor="false"
          data-testid={testID || 'pp-text-area'}
        />
      </InputWrapper>
      <PropertyPalError isVisible={isInErrorState} error={meta?.error} className={cn(isInErrorState && errClassName)} />
    </div>
  );
};

export default PropertyPalTextArea;
